/**
 * Shared OpenGraph / Twitter card metadata for marketing pages.
 *
 * Every public marketing route builds its social tags through `socialMeta()` so
 * previews stay consistent: same site name, same 1200x630 branded image, same
 * card type, and og/twitter titles + descriptions that never drift apart.
 *
 * Note: `og:image` belongs on leaf routes only (never `__root.tsx`), otherwise
 * the root tag would override every child's preview.
 */

export const SITE_NAME = "MissionReady";
export const SITE_URL = "https://missionready.dev";

/** 1200x630 (1.91:1) — the aspect ratio Facebook/LinkedIn/X expect. */
export const OG_IMAGE = `${SITE_URL}/og-cover-wide.png`;
export const OG_IMAGE_WIDTH = "1200";
export const OG_IMAGE_HEIGHT = "630";
export const OG_IMAGE_ALT =
  "MissionReady — security operations platform for guard, patrol, and field teams";

export type SocialMetaInput = {
  /** Social headline. Defaults to the page <title> when omitted. */
  title: string;
  /** Social description, ideally 110-160 characters. */
  description: string;
  /** Absolute canonical URL of this page. */
  url: string;
  /** "website" for marketing pages, "article" for posts. */
  type?: "website" | "article" | "product";
  /** Absolute URL. Only override when the page has its own hero image. */
  image?: string;
  imageAlt?: string;
  imageWidth?: string;
  imageHeight?: string;
};

export function socialMeta({
  title,
  description,
  url,
  type = "website",
  image = OG_IMAGE,
  imageAlt = OG_IMAGE_ALT,
  imageWidth = OG_IMAGE_WIDTH,
  imageHeight = OG_IMAGE_HEIGHT,
}: SocialMetaInput) {
  return [
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: type },
    { property: "og:url", content: url },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:locale", content: "en_US" },
    { property: "og:image", content: image },
    { property: "og:image:secure_url", content: image },
    { property: "og:image:type", content: "image/png" },
    { property: "og:image:width", content: imageWidth },
    { property: "og:image:height", content: imageHeight },
    { property: "og:image:alt", content: imageAlt },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image },
    { name: "twitter:image:alt", content: imageAlt },
  ];
}
